/**
 * Parses time label and subtitle from instance name
 *
 * This function handles two main formats:
 * 1. Special events with subtitle: "September 7, 2025: 9:30 AM- Gabby's Dollhouse MEOWseum Discovery Day: 9:30 AM"
 * 2. Regular events without subtitle: "September 6, 2025: 2:30 PM"
 *
 * @param instanceName - The full instance name string
 * @returns Object with timeLabel and subtitle
 *
 * @example
 * // Special Event with subtitle
 * parseInstanceName("September 7, 2025: 9:30 AM- Gabby's Dollhouse MEOWseum Discovery Day: 9:30 AM")
 * // Returns: { timeLabel: "9:30 AM", subtitle: "Gabby's Dollhouse MEOWseum Discovery Day: 9:30 AM" }
 *
 * // Regular Event without subtitle
 * parseInstanceName("September 6, 2025: 2:30 PM")
 * // Returns: { timeLabel: "2:30 PM", subtitle: "" }
 *
 * // Invalid input
 * parseInstanceName("")
 * // Returns: { timeLabel: "", subtitle: "" }
 */
const parseInstanceName = (instanceName: string): { timeLabel: string; subtitle: string } => {
  // Handle edge cases - return empty values for invalid inputs
  if (!instanceName || typeof instanceName !== 'string') {
    return {
      timeLabel: '',
      subtitle: '',
    };
  }

  // Clean the input string by trimming whitespace
  const trimmedName = instanceName.trim();

  // Check if the instance name contains a subtitle (indicated by hyphen)
  if (!trimmedName.includes('-')) {
    // No subtitle found - extract time from the full string
    const timeMatch = trimmedName.match(/(\d{1,2}:\d{2}\s*(?:AM|PM))/i);
    const timeLabel = timeMatch ? timeMatch[1].trim() : '';

    return {
      timeLabel,
      subtitle: '',
    };
  }

  // Split by hyphen to separate time and subtitle
  const parts = trimmedName.split('-');

  // Ensure we have at least 2 parts after splitting
  if (parts.length < 2) {
    const timeMatch = trimmedName.match(/(\d{1,2}:\d{2}\s*(?:AM|PM))/i);
    const timeLabel = timeMatch ? timeMatch[1].trim() : '';

    return {
      timeLabel,
      subtitle: '',
    };
  }

  // Extract time from the first part (before the first hyphen)
  const timePart = parts[0].trim();
  const timeMatch = timePart.match(/(\d{1,2}:\d{2}\s*(?:AM|PM))/i);
  const timeLabel = timeMatch ? timeMatch[1].trim() : '';

  // Extract subtitle from the last part (after the last hyphen)
  const subtitle = parts[parts.length - 1].trim();

  return {
    timeLabel,
    subtitle,
  };
};

export default parseInstanceName;
